import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Pencil, Trash2, ChevronDown } from "lucide-react";

const Faqs = ({ isModalOpen, setIsModalOpen }) => {
  const [formData, setFormData] = useState({ title: "", content: "" });
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, faqId: null });
  const [openFaqId, setOpenFaqId] = useState(null);
  const orgName = sessionStorage.getItem("organizationName");
  const userRole = sessionStorage.getItem(`${orgName}_role`);
  const isAdmin = userRole === "admin" || userRole === "superadmin";

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem(`${orgName}_token`)}`,
    },
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/faq/getFaq`,
        authHeaders
      );
      setFaqs(response.data);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaq) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/faq/updateFaq/${editingFaq.id}`,
          formData,
          authHeaders
        );
        toast.success("FAQ updated successfully");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/faq/insertFaq`,
          formData,
          authHeaders
        );
        toast.success("FAQ added successfully");
      }
      setFormData({ title: "", content: "" });
      setEditingFaq(null);
      setIsModalOpen(false);
      fetchFaqs();
    } catch (error) {
      console.error("Error submitting FAQ:", error);
      toast.error("Error saving FAQ");
    }
  };

  const handleDelete = async (faqId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/faq/deleteFaq/${faqId}`,
        authHeaders
      );
      toast.success("FAQ deleted successfully");
      setConfirmDialog({ open: false, faqId: null });
      fetchFaqs();
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("Error deleting FAQ");
    }
  };

  const openEditModal = (faq) => {
    setEditingFaq(faq);
    setFormData({ title: faq.title, content: faq.content });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingFaq(null);
    setFormData({ title: "", content: "" });
  };

  const toggleFaq = (faqId) => {
    setOpenFaqId(openFaqId === faqId ? null : faqId);
  };

  return (
    <div className="max-w-3xl mx-auto px-5">
      <div className="flex flex-col items-center mb-8">
        <h2 className="font-bold text-3xl tracking-tight text-gray-800">FAQ's</h2>
        <p className="text-neutral-500 text-base mt-2">Frequently asked questions</p>
      </div>

      {faqs.length === 0 ? (
        <p className="text-center text-gray-400 py-8">No FAQs added yet.</p>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
            >
              <div
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleFaq(faq.id)}
              >
                <span className="font-medium text-gray-800 min-w-20 flex-1 truncate mr-4">{faq.title}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isAdmin && (
                    <>
                      <button
                        title="Edit"
                        className="p-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={(e) => { e.stopPropagation(); openEditModal(faq); }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        title="Delete"
                        className="p-1.5 rounded bg-red-700 hover:bg-red-800 text-white"
                        onClick={(e) => { e.stopPropagation(); setConfirmDialog({ open: true, faqId: faq.id }); }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                  <ChevronDown
                    size={20}
                    className={`text-gray-400 transition-transform duration-200 ${openFaqId === faq.id ? "rotate-180" : ""}`}
                  />
                </div>
              </div>
              {openFaqId === faq.id && (
                <div className="px-5 pb-4 text-gray-600 border-t border-gray-100 pt-3">
                  {faq.content}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit FAQ Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {editingFaq ? "Edit FAQ" : "Create FAQ"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="title" className="block mb-1 text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Enter title"
                  required
                />
              </div>
              <div>
                <label htmlFor="content" className="block mb-1 text-sm font-medium text-gray-700">
                  Content
                </label>
                <textarea
                  name="content"
                  id="content"
                  rows={4}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Enter content"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
                >
                  {editingFaq ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete FAQ</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this FAQ? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                onClick={() => setConfirmDialog({ open: false, faqId: null })}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                onClick={() => handleDelete(confirmDialog.faqId)}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Faqs;
